import {
  ChangeEvent,
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useState,
} from "react";
import cookie from "js-cookie";
import Head from "next/head";
import {
  AnnouncementBar,
  BackToTopButton,
  Country,
  CountrySelector,
  CountrySelectorProps,
  InfoBlock,
  LayoutWithNav,
  Nav,
  NavProps,
  useScreenSizeContext,
} from "@bluebottlecoffee/design-system/components";
import { useAnalytics, useCart } from "@chordcommerce/react-autonomy";
import { useRouter } from "next/router";
import { useLiveQuery } from "next-sanity/preview";
import { trackEvent } from "../lib/segment/track-event";
import { toNavAndCartInfoBannerProps } from "../lib/transformers/info-block";
import { announcementBarQuery, BaseProps } from "../lib/sanity/shared";
import { toAnnouncementBarProps } from "../lib/transformers/announcement-bar";
import { toFooterProps } from "../lib/transformers/footer";
import { toNavProps } from "../lib/transformers/nav";
import { LoginDialog } from "./LoginDialog";
import { homePage } from "../lib/link-builders";
import { toMetaTagProps } from "../lib/transformers/metaTag";
import {
  AnnouncementBar as AnnouncementBarSchema,
  Cart,
  ImageWithAltText,
  MetaTags as MetaTagsSchema,
} from "../lib/sanity-schema";
import { nestlePrivacyToggle } from "./nestlePrivacyToggle";
import { handleEmailOptIn } from "../lib/component-utils/email-opt-in";
import {
  SearchDialog,
  SearchDialogCopy,
  SearchCategoriesProps,
} from "./SearchDialog";
import { transformProductRecsToShopCards } from "../lib/transformers/product-recs";
import { productRecsWithBestValueVariant } from "../lib/utils/product-recs/product-recs";
import localization, { Region } from "../localization";
import { isFeatureEnabled } from "../lib/utils/is-feature-enabled";
import { CartReminder } from "./CartReminder";
import { LoginCopy } from "./Login";

type LayoutWrapperProps = PropsWithChildren<
  Omit<BaseProps, "appPlayLinks"> & {
    altBBLogo?: ImageWithAltText;
    draftMode?: boolean;
    isTransparent?: boolean;
    loginDialogCopy: LoginCopy;
    metaTags?: MetaTagsSchema;
    pageTitle?: string;
    productSearch: SearchDialogCopy & SearchCategoriesProps;
    slideOutContent?: ReactNode[];
    cart?: Cart;
    openMiniCartPanel?: boolean;
  }
>;

export type BaseNavProps = Omit<NavProps, "brandLink">;

export const LayoutWrapper: FunctionComponent<LayoutWrapperProps> = ({
  accountMenuCopy,
  altBBLogo,
  announcementBar,
  aria,
  children,
  draftMode,
  footer,
  isTransparent,
  lang,
  loginDialogCopy,
  metaTags,
  nav,
  navAndCartInfoBanner,
  pageTitle,
  productRecs,
  productSearch,
  region,
  slideOutContent,
  cart: cartCopy,
  openMiniCartPanel,
}) => {
  const { backToTopAriaLabel, brandLinkLabel, cartLinkLabel, skipToContent } =
    aria;
  const { cart, forgetCart, loadCart } = useCart();
  const { trackProductClicked } = useAnalytics();

  const [infoBannerCookie, setInfoBannerCookie] = useState<boolean>(true);
  useEffect(() => {
    loadCart();
  }, [loadCart]);

  // use state to delay info banner render until `.get`
  useEffect(() => {
    const hasInfoBannerCookie = cookie.get("info_banner_dismissed") === "true";
    setInfoBannerCookie(hasInfoBannerCookie);
  }, []);

  const announcementBarProps = draftMode
    ? useLiveQuery<AnnouncementBarSchema>(
        announcementBar,
        announcementBarQuery,
      )[0]
    : announcementBar;

  const cartItemCount =
    cart?.lineItems?.reduce((result, li) => result + li.quantity, 0) ?? 0;

  // Create a set of footerProps
  const footerProps = toFooterProps(footer, region, lang, (email) =>
    handleEmailOptIn(email),
  );

  // Info banner dismiss handler (set cookie)
  const handleInfoBannerDismiss = () => {
    cookie.set("info_banner_dismissed", "true");
    setInfoBannerCookie(true);
  };

  // Skip link props
  const skipLinkProps = [
    {
      linkToId: "main",
      text: skipToContent ?? "Skip to content",
    },
  ];

  // Push a "Do Not Sell My Personal Information" link onto the finePrint array
  footerProps.finePrint.push({
    url: "#",
    isFunctionLink: true,
    onClick: () => {
      window.OneTrust?.ToggleInfoDisplay();
    },
    text: `Your Privacy Choices ${nestlePrivacyToggle}`,
  });

  const transformedProductRecs = transformProductRecsToShopCards(
    productRecsWithBestValueVariant(productRecs, lang),
    lang,
    region,
    (product) => trackProductClicked(product),
  );

  const { isMobile } = useScreenSizeContext();
  const { asPath } = useRouter();
  const isAccountPage = asPath.includes("/account");
  const isProductPage = asPath.includes("/product/");
  const isProductPageMobile = isProductPage && isMobile();

  const countries: CountrySelectorProps["options"] = localization
    .regionNames()
    .map((regionName) => ({
      value: regionName,
      display: regionName,
      ariaLabel: localization.regionLongName(regionName),
    }));

  const onSelectCountry = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedCountry = e.target.value as Region;
    const defaultLanguage = localization.defaultLanguage(selectedCountry);
    // clears cart data from the storefront's state and local storage
    forgetCart();
    switch (selectedCountry) {
      case "jp":
        window.location.href = "https://store.bluebottlecoffee.jp/";
        break;
      case "kr":
        window.location.href = "https://kr.bluebottlecoffee.com/";
        break;
      case "ca":
      case "us":
      default:
        // cookie will be checked by url resolver, and needs to be uppercase to match
        // https://github.com/bluebottlecoffee/worker-region-router/blob/main/src/url-resolver.js#L8
        cookie.set("selectedCountry", selectedCountry.toUpperCase());
        window.location.href = `/${selectedCountry}/${defaultLanguage.code}`;
    }
  };

  const countrySelectorProps = {
    defaultSelectedOption: region as Country,
    options: countries,
    onSelect: onSelectCountry,
  };

  const isCountrySelectorEnabled = isFeatureEnabled(
    process.env.NEXT_PUBLIC_ENABLE_COUNTRY_SELECTOR,
  );

  const navProps = toNavProps(
    isTransparent,
    { cartItemCount, cartLinkLabel },
    nav,
    region,
    lang,
    altBBLogo,
  );

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        {metaTags?.map((tag) => (
          <meta {...toMetaTagProps(tag, lang)} />
        ))}
      </Head>

      <LayoutWithNav
        transparentNav={isTransparent && nav.transparentMode}
        announcements={
          announcementBar?.announcements?.length > 0 && (
            <AnnouncementBar
              {...toAnnouncementBarProps(announcementBarProps, lang)}
              sideContent={
                isCountrySelectorEnabled ? (
                  <CountrySelector {...countrySelectorProps} />
                ) : null
              }
            />
          )
        }
        infoBanner={
          !infoBannerCookie &&
          navAndCartInfoBanner &&
          navAndCartInfoBanner.showInfoBanners && (
            <InfoBlock
              {...toNavAndCartInfoBannerProps({
                data: navAndCartInfoBanner,
                lang,
              })}
              centered
              dismiss={{
                ariaLabel: navAndCartInfoBanner.dismissAriaLabel[lang],
                onDismiss: handleInfoBannerDismiss,
              }}
            />
          )
        }
        nav={
          nav?.navItems?.length > 0 && (
            <Nav
              {...navProps}
              ariaLabels={{
                ...aria,
                cartNavMenuButtonLabel: `${aria.cartNavMenuButtonLabel}: ${cartItemCount}`,
              }}
              brandLink={{
                text: brandLinkLabel,
                url: homePage({ region, lang }),
              }}
              skipLinks={skipLinkProps}
              countrySelector={
                isCountrySelectorEnabled ? countrySelectorProps : undefined
              }
              openMiniCartPanel={
                isFeatureEnabled(process.env.NEXT_PUBLIC_ENABLE_ONLY_SIDECART)
                  ? openMiniCartPanel
                  : false
              }
              trackEvent={trackEvent}
            />
          )
        }
        cartReminder={
          <CartReminder cartCopy={cartCopy} region={region} lang={lang} />
        }
        sticky="navAndAnnouncements"
        bgColor="white"
        footer={footerProps}
        // OneTrust renders the cookie banner.
        // Explicitly pass undefined until the design-system makes
        // this prop optional.
        cookiesNotice={undefined}
      >
        <>
          {[
            ...slideOutContent,
            <LoginDialog
              copy={loginDialogCopy}
              accountMenuCopy={accountMenuCopy}
              region={region}
              lang={lang}
            />,
            <SearchDialog
              {...productSearch}
              lang={lang}
              region={region}
              noResultsShopCards={transformedProductRecs}
            />,
          ]}
          {children}
          {!isAccountPage && !isProductPageMobile && (
            <BackToTopButton
              ariaLabel={backToTopAriaLabel}
              topThreshold={870}
              botThreshold={160}
            />
          )}
        </>
      </LayoutWithNav>
    </>
  );
};

LayoutWrapper.defaultProps = {
  altBBLogo: undefined,
  isTransparent: false,
  metaTags: [],
  pageTitle: "Blue Bottle Coffee",
  slideOutContent: [],
  cart: undefined,
  openMiniCartPanel: false,
};
